import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ChevronDown } from "lucide-react";
import { hero, site, waLink } from "@/content/siteContent";
import { scrollToSection } from "@/lib/smoothScroll";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Hero() {
  const ref = useRef(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <section id="inicio" ref={ref} className="relative flex min-h-[100svh] flex-col overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 scale-110">
        <img
          src={hero.image}
          alt="Técnico da Sagarelo em serviço de climatização em altura"
          className="h-full w-full object-cover"
          data-testid="hero-image"
        />
      </motion.div>
      <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(13,13,15,.97), rgba(13,13,15,.72), rgba(13,13,15,.22))" }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(0deg, rgba(13,13,15,1) 0%, rgba(13,13,15,0) 30%)" }} />
      <div
        className="pointer-events-none absolute right-[8%] top-[28%] h-[380px] w-[380px] rounded-full opacity-25 blur-[110px]"
        style={{ background: "radial-gradient(circle, rgba(245,176,0,.5), transparent 65%)" }}
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-5 pb-24 pt-36 md:px-8">
        <div className="mb-7 flex items-center gap-4">
          <span className="h-px w-12 bg-brand" />
          <p className="microtitle" data-testid="hero-microtitle">{hero.microtitle}</p>
        </div>

        <h1 className="font-display text-[2.7rem] font-extrabold leading-[0.98] tracking-tight text-paper sm:text-6xl lg:text-7xl xl:text-[5.4rem]" data-testid="hero-title">
          {hero.titleLines.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className="block"
                initial={{ y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
              >
                {line.includes(hero.highlightWord) ? (
                  <>
                    {line.replace(hero.highlightWord, "")}
                    <span className="text-brand">{hero.highlightWord}</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-mist md:text-lg"
          data-testid="hero-subtitle"
        >
          {hero.subtitle}
        </motion.p>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-5 font-display text-[0.68rem] font-bold tracking-[0.2em] text-steel"
          data-testid="hero-tags-line"
        >
          {hero.tagsLine}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row"
        >
          <button onClick={() => setQuoteOpen(true)} className="btn-industrial" data-testid="hero-online-quote-button">
            {hero.onlineQuote.cta}
            <ArrowRight className="h-4 w-4" />
          </button>
          <a href={waLink()} target="_blank" rel="noopener noreferrer" className="btn-outline-industrial" data-testid="hero-quote-button">
            {hero.primaryCta}
            <ArrowRight className="h-4 w-4" />
          </a>
          <button onClick={() => scrollToSection("#servicos")} className="btn-outline-industrial" data-testid="hero-services-button">
            {hero.secondaryCta}
          </button>
        </motion.div>
      </motion.div>

      <Dialog open={quoteOpen} onOpenChange={setQuoteOpen}>
        <DialogContent className="border-metal bg-graphite text-paper sm:max-w-md" data-testid="hero-quote-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold tracking-tight text-paper">
              {hero.onlineQuote.titleA} <span className="text-brand">{hero.onlineQuote.titleB}</span>
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-mist md:text-base" data-testid="hero-quote-modal-text">
              {hero.onlineQuote.message}
            </DialogDescription>
          </DialogHeader>
          <button onClick={() => window.location.assign("/contratar")} className="btn-industrial mt-2 w-full justify-center" data-testid="hero-quote-proceed-button">
            {hero.onlineQuote.proceed}
            <ArrowRight className="h-4 w-4" />
          </button>
        </DialogContent>
      </Dialog>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.3, duration: 0.8 }}
        className="relative z-10 border-t border-metal/70"
        data-testid="hero-bottom-bar"
      >
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3 px-5 py-5 md:px-8">
          <p className="text-[0.68rem] font-bold tracking-[0.22em] text-mist">
            {site.schedule}
          </p>
          <p className="flex items-center gap-3 text-[0.68rem] font-bold tracking-[0.22em] text-brand">
            {site.region}
            <ChevronDown className="h-4 w-4 animate-bounce text-steel" />
          </p>
        </div>
      </motion.div>
    </section>
  );
}
